/**
 * The guided tour — design/PROTOTYPE-SPEC.md §15, ported from
 * design/Clinic Stock v2.dc.html:4228.
 *
 * ## Why this is more than a tooltip
 *
 * The spotlight is measured against a live element on a real page, so every
 * timing problem in the DOM is in scope: the page has to navigate first, the
 * element may not have rendered yet, scrolling it into view moves it, and the
 * window can be resized mid-tour. The prototype's answer — which this keeps —
 * is a retry loop and two deliberate delays, and the delays are load-bearing:
 *
 *   - **240ms** after a step change, so React has committed the new page before
 *     anything is queried. Without it the first step of every page measures the
 *     *previous* page's element, or nothing at all.
 *   - **460ms** after a smooth scroll, because the rect keeps moving while the
 *     scroll animates. Measuring early rings where the element used to be.
 *   - **5 retries** while the element is missing, for pages that fetch before
 *     they render. After that the spotlight is dropped and the card is shown
 *     unanchored rather than pointing at nothing.
 *
 * All the arithmetic lives in `geometry.ts` and is unit-tested; this file is
 * the part that needs a browser.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { c, mono, primaryButton, sans } from '../../theme.js';
import {
  CARD_HEIGHT,
  CARD_WIDTH,
  clampToViewport,
  placeCard,
  rectChanged,
  scrollTargetFor,
  type Rect,
} from './geometry.js';
import type { TourStep } from './steps.js';

/** React must have committed the new page before we query for its elements. */
const AFTER_STEP_MS = 240;
/** A smooth scroll is still animating; the rect keeps moving until it settles. */
const AFTER_SCROLL_MS = 460;
const MAX_ATTEMPTS = 5;

interface Props {
  /**
   * What this run is called — "Quick tour", or a guide's title. Shown above the
   * step counter. A guide labelled "Quick tour" tells the reader they are in
   * something other than what they clicked.
   */
  label: string;
  steps: TourStep[];
  index: number;
  onIndex: (i: number) => void;
  /** Navigates the shell. Called before measuring, hence the delay above. */
  onNavigate: (page: TourStep['page']) => void;
  onClose: () => void;
}

export function TourOverlay({ label, steps, index, onIndex, onNavigate, onClose }: Props) {
  const [rect, setRect] = useState<Rect | null>(null);
  const rectRef = useRef<Rect | null>(null);
  rectRef.current = rect;

  const step = steps[index];
  const timers = useRef<number[]>([]);
  const observer = useRef<ResizeObserver | null>(null);

  const clearTimers = () => {
    for (const t of timers.current) window.clearTimeout(t);
    timers.current = [];
    observer.current?.disconnect();
    observer.current = null;
  };

  const measure = useCallback(
    (anchor: string, attempt = 0) => {
      const el = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);

      if (!el) {
        // The page may still be fetching. Retry a few times, then give up and
        // show the card unanchored — better than a ring around nothing.
        if (attempt < MAX_ATTEMPTS) {
          timers.current.push(
            window.setTimeout(() => measure(anchor, attempt + 1), AFTER_STEP_MS),
          );
        } else if (rectRef.current) {
          setRect(null);
        }
        return;
      }

      const box = el.getBoundingClientRect();
      const viewport = { width: window.innerWidth, height: window.innerHeight };

      const scrollTo = scrollTargetFor(box, window.scrollY, viewport);
      if (scrollTo !== null) {
        window.scrollTo({ top: scrollTo, behavior: 'smooth' });
      }

      // Re-query rather than reuse `el`: a re-render between the scroll and the
      // measure can replace the node, and measuring a detached one gives zeros.
      timers.current.push(
        window.setTimeout(
          () => {
            const again = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
            if (!again) return;
            const next = clampToViewport(again.getBoundingClientRect(), {
              width: window.innerWidth,
              height: window.innerHeight,
            });
            // Only set when it actually moved — an unconditional setState here
            // re-renders, re-measures, and loops forever.
            if (rectChanged(rectRef.current, next)) setRect(next);

            /**
             * The retries above handle an element that does not exist yet. They
             * do NOT handle one that exists at the wrong size — a table whose
             * rows are still loading is present, measurable, and 127px wide
             * instead of 1212. The spotlight then rings a sliver of it.
             *
             * Observing the element catches exactly that: when the data lands
             * and it grows, the ring follows. The prototype has this bug; it is
             * only visible on a page that fetches after it renders.
             */
            observer.current?.disconnect();
            observer.current = new ResizeObserver(() => {
              const live = document.querySelector<HTMLElement>(`[data-tour="${anchor}"]`);
              if (!live) return;
              const grown = clampToViewport(live.getBoundingClientRect(), {
                width: window.innerWidth,
                height: window.innerHeight,
              });
              if (rectChanged(rectRef.current, grown)) setRect(grown);
            });
            observer.current.observe(again);
          },
          scrollTo !== null ? AFTER_SCROLL_MS : 0,
        ),
      );
    },
    [],
  );

  // Navigate, then measure. Runs on every step change.
  useEffect(() => {
    if (!step) return;
    clearTimers();
    onNavigate(step.page);
    timers.current.push(window.setTimeout(() => measure(step.anchor), AFTER_STEP_MS));
    return clearTimers;
    // NOTE: `onNavigate` is deliberately not a dependency. The shell recreates
    // it on every render, so depending on it would re-run this effect
    // continuously — navigating and re-measuring forever.
  }, [step, measure]);

  // Re-measure on resize. A rotated tablet otherwise keeps the old rect.
  useEffect(() => {
    if (!step) return;
    const onResize = () => measure(step.anchor);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [step, measure]);

  // Escape ends the tour. A modal-ish overlay that traps someone is worse than
  // no tour at all.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onIndex(Math.min(index + 1, steps.length - 1));
      if (e.key === 'ArrowLeft') onIndex(Math.max(index - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, steps.length, onIndex, onClose]);

  if (!step) return null;

  const last = index === steps.length - 1;
  const viewport = { width: window.innerWidth, height: window.innerHeight };
  const placement = rect
    ? placeCard(rect, viewport)
    : // Unanchored fallback: bottom-left, clear of the sidebar.
      { left: 266, top: viewport.height - CARD_HEIGHT - 26, side: 'right' as const };

  return (
    <>
      {rect ? (
        <div
          aria-hidden
          style={{
            position: 'fixed',
            left: rect.x - 6,
            top: rect.y - 6,
            width: rect.w + 12,
            height: rect.h + 12,
            borderRadius: 18,
            // The 9999px spread is what dims everything else: one element, no
            // four-panel backdrop to keep in sync with the hole.
            boxShadow:
              '0 0 0 9999px rgba(20,26,22,0.45), 0 0 0 2px #CFE0D4, 0 0 26px rgba(207,224,212,0.4)',
            zIndex: 74,
            pointerEvents: 'none',
            transition: 'all 0.35s cubic-bezier(0.22,1,0.36,1)',
          }}
        />
      ) : (
        // No anchor found: still dim the page, so the card reads as modal.
        <div
          aria-hidden
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(20,26,22,0.45)',
            zIndex: 74,
            pointerEvents: 'none',
          }}
        />
      )}

      <div
        role="dialog"
        aria-label={`Tour: ${step.title}`}
        style={{
          position: 'fixed',
          left: placement.left,
          top: placement.top,
          width: CARD_WIDTH,
          zIndex: 75,
          background: c.ink,
          color: c.onDark,
          borderRadius: 18,
          padding: '22px 24px',
          boxShadow: '0 24px 60px rgba(20,26,22,0.55)',
          fontFamily: sans,
          transition: 'left 0.35s cubic-bezier(0.22,1,0.36,1), top 0.35s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div
          style={{
            fontSize: 10,
            textTransform: 'lowercase', fontFamily: mono,
            color: c.green,
            marginBottom: 9,
          }}
        >
          {label} · {index + 1} of {steps.length}
        </div>

        <h2
          style={{
            fontFamily: sans, letterSpacing: '-0.03em',
            fontWeight: 300,
            fontSize: 21,
            margin: '0 0 8px',
            color: c.onDarkBright,
          }}
        >
          {step.title}
        </h2>
        <p style={{ fontSize: 13.5, lineHeight: 1.7, color: c.onDarkMuted, margin: '0 0 18px' }}>
          {step.body}
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={() => (last ? onClose() : onIndex(index + 1))}
            style={{ ...primaryButton, width: 'auto', height: 40, padding: '0 18px' }}
          >
            {last ? 'Finish' : 'Next'}
          </button>
          {index > 0 && (
            <button type="button" onClick={() => onIndex(index - 1)} style={ghost}>
              Back
            </button>
          )}
          <button type="button" onClick={onClose} style={{ ...ghost, marginLeft: 'auto' }}>
            Skip
          </button>
        </div>
      </div>
    </>
  );
}

const ghost: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 13,
  fontFamily: sans,
  color: c.onDarkFaint,
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  padding: 0,
};
